/**
 * Typed API client.
 *
 * The access token is held in a module-scoped variable, never in localStorage —
 * anything that can run script on the page can read localStorage, and a token
 * there survives the tab. The refresh token is an httpOnly cookie the browser
 * sends automatically and this code cannot see at all.
 */

import type {
  AdminCampaignRow, AdsOverview, AdvertiserSummary, AnomalyRow, BagEntry,
  BillableLine, CampaignInput, CampaignReport, CampaignView, InvoiceSettings,
  InvoiceView, ItemDef, NpcView, PendingAsset,
  QuestView, SavedOfferView, ServedAd, ShopView, SlotOffer, TurnInResult
} from './gameTypes.js';
import type {
  ApiResponse, Appearance, AuthSession, CharacterSummary, ClassDef, ClassKey,
  PublicUser, RegisterResponse, ViewClaim, VisibilityRules
} from '@emberreach/shared';

let accessToken: string | null = null;
let accessTokenExpiresAt = 0;
/** Shared so ten parallel 401s trigger one refresh, not ten. */
let refreshInFlight: Promise<boolean> | null = null;

export class ApiError extends Error {
  readonly code: string;
  readonly fields: ReadonlyArray<{ field: string; message: string }>;
  readonly status: number;

  constructor(status: number, code: string, message: string, fields: ReadonlyArray<{ field: string; message: string }> = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }

  /** Message for a named form field, if the server flagged one. */
  fieldError(field: string): string | undefined {
    return this.fields.find((f) => f.field === field)?.message;
  }
}

function setSession(session: AuthSession): void {
  accessToken = session.accessToken;
  accessTokenExpiresAt = session.accessTokenExpiresAt;
}

export function clearSession(): void {
  accessToken = null;
  accessTokenExpiresAt = 0;
}

export function isSignedIn(): boolean {
  return accessToken !== null;
}

/**
 * The current access token, for handing to the game socket.
 *
 * Refreshes first if it is close to expiring — a room join carries the token
 * once at connect time and has no way to re-present a fresher one, so joining
 * with 20 seconds left would drop the player almost immediately.
 */
export async function accessTokenForSocket(): Promise<string> {
  if (needsRefresh()) await refreshSession();
  if (accessToken === null) throw new ApiError(401, 'unauthenticated', 'Sign in to play.');
  return accessToken;
}

/** True when the token is gone or about to expire. 60 s of slack for a slow link. */
function needsRefresh(): boolean {
  return accessToken === null || Date.now() / 1000 > accessTokenExpiresAt - 60;
}

async function parse<T>(res: Response): Promise<T> {
  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(res.status, 'internal_error', 'The server sent something we could not read.');
  }
  if (body.ok) return body.data;
  throw new ApiError(res.status, body.error.code, body.error.message, body.error.fields ?? []);
}

async function refreshSession(): Promise<boolean> {
  if (refreshInFlight !== null) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });
      if (!res.ok) { clearSession(); return false; }
      setSession(await parse<AuthSession>(res));
      return true;
    } catch {
      clearSession();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Set false for endpoints that must not attempt a refresh (login, refresh). */
  auth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = options;

  if (auth && needsRefresh() && accessToken !== null) await refreshSession();

  const send = async (): Promise<Response> =>
    fetch(path, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(auth && accessToken !== null ? { Authorization: `Bearer ${accessToken}` } : {})
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {})
    });

  let res = await send();

  // One retry after a refresh — a token can expire between the check and the send.
  if (auth && res.status === 401 && accessToken !== null) {
    if (await refreshSession()) res = await send();
  }

  return parse<T>(res);
}

// --- endpoints -------------------------------------------------------------

export const api = {
  health: () => request<{ status: string; version: string }>('/api/health', { auth: false }),

  register: (email: string, password: string, displayName: string) =>
    request<RegisterResponse>('/api/auth/register', {
      method: 'POST',
      auth: false,
      body: { email, password, displayName }
    }),

  async login(email: string, password: string): Promise<PublicUser> {
    const session = await request<AuthSession>('/api/auth/login', {
      method: 'POST',
      auth: false,
      body: { email, password }
    });
    setSession(session);
    return session.user;
  },

  async restore(): Promise<PublicUser | null> {
    // Called once at start-up. The refresh cookie survives a reload, so a
    // returning player skips the sign-in screen.
    if (!(await refreshSession())) return null;
    try {
      const { user } = await request<{ user: PublicUser }>('/api/auth/me');
      return user;
    } catch {
      clearSession();
      return null;
    }
  },

  me: () => request<{ user: PublicUser }>('/api/auth/me'),

  async logout(everywhere = false): Promise<void> {
    try {
      await request<{ signedOut: boolean }>('/api/auth/logout', {
        method: 'POST',
        auth: false,
        body: { everywhere }
      });
    } finally {
      clearSession();
    }
  },

  // --- characters ---
  characterOptions: () =>
    request<{
      classes: ClassDef[];
      appearance: {
        bodyTypes: readonly string[]; faces: readonly string[]; hair: readonly string[];
        hairColours: readonly string[]; skinTones: readonly string[]; outfits: readonly string[];
        defaults: Appearance;
      };
      maxCharacters: number;
      zones: { id: string; name: string; blurb: string; kind: string }[];
    }>('/api/characters/options', { auth: false }),

  listCharacters: () =>
    request<{ characters: CharacterSummary[] }>('/api/characters'),

  createCharacter: (name: string, classKey: ClassKey, appearance: Appearance) =>
    request<{ character: CharacterSummary }>('/api/characters', {
      method: 'POST',
      body: { name, classKey, appearance }
    }),

  deleteCharacter: (id: string) =>
    request<{ deleted: boolean }>(`/api/characters/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // --- game ---
  inventory: (characterId: string) =>
    request<{
      bag: BagEntry[];
      equipment: Record<string, ItemDef>;
      stats: { attack: number; defence: number };
      marks: number;
      bagSlots: number;
    }>(`/api/game/${characterId}/inventory`),

  equip: (characterId: string, itemKey: string) =>
    request<{ equipment: Record<string, ItemDef> }>(
      `/api/game/${characterId}/inventory/equip`, { method: 'POST', body: { itemKey } }),

  unequip: (characterId: string, slot: string) =>
    request<{ equipment: Record<string, ItemDef> }>(
      `/api/game/${characterId}/inventory/unequip`, { method: 'POST', body: { slot } }),

  useItem: (characterId: string, itemKey: string) =>
    request<{ health: number; maxHealth: number; stamina: number; maxStamina: number }>(
      `/api/game/${characterId}/inventory/use`, { method: 'POST', body: { itemKey } }),

  quests: (characterId: string) =>
    request<{ quests: QuestView[] }>(`/api/game/${characterId}/quests`),

  acceptQuest: (characterId: string, questKey: string) =>
    request<{ quests: QuestView[] }>(
      `/api/game/${characterId}/quests/accept`, { method: 'POST', body: { questKey } }),

  turnInQuest: (characterId: string, questKey: string, npcKey: string) =>
    request<{ result: TurnInResult; quests: QuestView[] }>(
      `/api/game/${characterId}/quests/turn-in`, { method: 'POST', body: { questKey, npcKey } }),

  npcs: (zoneId: string) =>
    request<{ npcs: NpcView[] }>(`/api/game/npcs/${zoneId}`, { auth: false }),

  talk: (characterId: string, npcKey: string) =>
    request<{
      npc: { key: string; name: string; greeting: string; shopKey: string | null };
      offers: QuestView[];
      turnIns: QuestView[];
      inProgress: QuestView[];
    }>(`/api/game/${characterId}/talk`, { method: 'POST', body: { npcKey } }),

  shop: (shopKey: string) =>
    request<{ shop: ShopView }>(`/api/game/shops/${shopKey}`, { auth: false }),

  buy: (characterId: string, shopKey: string, itemKey: string, quantity: number) =>
    request<{ marks: number; spent: number; bag: BagEntry[] }>(
      `/api/game/${characterId}/shops/${shopKey}/buy`,
      { method: 'POST', body: { itemKey, quantity } }),

  sellItem: (characterId: string, shopKey: string, itemKey: string, quantity: number) =>
    request<{ marks: number; earned: number; bag: BagEntry[] }>(
      `/api/game/${characterId}/shops/${shopKey}/sell`,
      { method: 'POST', body: { itemKey, quantity } }),

  // --- advertising ---
  /* Note the shape of this section: the game asks what to draw and reports
     what was seen, and it can keep an offer. It is never handed a link. The
     one endpoint that returns a destination URL is the offers list, which is
     read outside the game. */

  zoneAds: (characterId: string, zoneId: string) =>
    request<{ ads: ServedAd[]; rules: VisibilityRules }>(
      `/api/ads/${encodeURIComponent(characterId)}/zone/${encodeURIComponent(zoneId)}`),

  reportViews: (characterId: string, zoneId: string, views: ViewClaim[]) =>
    request<{ counted: number; rejected: number }>(
      `/api/ads/${encodeURIComponent(characterId)}/views`,
      { method: 'POST', body: { zoneId, views } }),

  openedPanel: (characterId: string, campaignId: string, slotId: string, zoneId: string) =>
    request<{ opened: boolean }>(
      `/api/ads/${encodeURIComponent(characterId)}/panel`,
      { method: 'POST', body: { campaignId, slotId, zoneId } }),

  keepOffer: (characterId: string, campaignId: string, slotId: string, zoneId: string) =>
    request<{ saved: boolean; offerId: string }>(
      `/api/ads/${encodeURIComponent(characterId)}/keep`,
      { method: 'POST', body: { campaignId, slotId, zoneId } }),

  savedOffers: () => request<{ offers: SavedOfferView[] }>('/api/offers'),

  openSavedOffer: (id: string) =>
    request<{ opened: boolean }>(`/api/offers/${encodeURIComponent(id)}/open`, { method: 'POST' }),

  removeSavedOffer: (id: string) =>
    request<{ removed: boolean }>(`/api/offers/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // --- advertiser portal ---
  /* Note there is no advertiserId parameter anywhere below. The server works
     it out from the signed-in user; a client that could name one is a client
     somebody would try renaming. */

  myAdvertisers: () => request<{ advertisers: AdvertiserSummary[] }>('/api/portal/me'),

  bookableSlots: () => request<{ slots: SlotOffer[] }>('/api/portal/slots'),

  myCampaigns: () => request<{ campaigns: CampaignView[] }>('/api/portal/campaigns'),

  myCampaign: (id: string) =>
    request<{ campaign: CampaignView }>(`/api/portal/campaigns/${encodeURIComponent(id)}`),

  createCampaign: (input: CampaignInput) =>
    request<{ campaign: CampaignView }>('/api/portal/campaigns',
      { method: 'POST', body: input }),

  updateCampaign: (id: string, input: CampaignInput) =>
    request<{ campaign: CampaignView }>(
      `/api/portal/campaigns/${encodeURIComponent(id)}`, { method: 'PATCH', body: input }),

  submitCampaign: (id: string) =>
    request<{ campaign: CampaignView }>(
      `/api/portal/campaigns/${encodeURIComponent(id)}/submit`, { method: 'POST' }),

  cancelCampaign: (id: string) =>
    request<{ campaign: CampaignView }>(
      `/api/portal/campaigns/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),

  campaignReport: (id: string) =>
    request<{ report: CampaignReport }>(
      `/api/portal/campaigns/${encodeURIComponent(id)}/report`),

  /**
   * Uploads artwork as raw bytes.
   *
   * Not multipart and not through `request`, because the body is a File rather
   * than JSON. The Content-Type is sent for politeness only — the server
   * decides what the file is from its bytes.
   */
  async uploadArtwork(
    campaignId: string, slotId: string, file: File, variant = 'main'
  ): Promise<{ url: string; status: string; message: string }> {
    if (needsRefresh() && accessToken !== null) await refreshSession();
    const res = await fetch(
      `/api/artwork/campaigns/${encodeURIComponent(campaignId)}/${variant}` +
      `?slotId=${encodeURIComponent(slotId)}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          ...(accessToken !== null ? { Authorization: `Bearer ${accessToken}` } : {})
        },
        body: file
      }
    );
    return parse<{ url: string; status: string; message: string }>(res);
  },

  // --- owner and staff ---
  adsOverview: () => request<{ overview: AdsOverview }>('/api/admin/ads/overview'),

  allCampaigns: (status = '') =>
    request<{ campaigns: AdminCampaignRow[] }>(
      `/api/admin/ads/campaigns${status === '' ? '' : `?status=${encodeURIComponent(status)}`}`),

  setCampaignStatus: (id: string, status: string, notes = '') =>
    request<{ status: string }>(
      `/api/admin/ads/campaigns/${encodeURIComponent(id)}/status`,
      { method: 'POST', body: { status, notes } }),

  setCampaignPrice: (id: string, billingModel: string, agreedAmountCents: number) =>
    request<{ priced: boolean }>(
      `/api/admin/ads/campaigns/${encodeURIComponent(id)}/price`,
      { method: 'POST', body: { billingModel, agreedAmountCents } }),

  pendingArtwork: () => request<{ assets: PendingAsset[] }>('/api/artwork/pending'),

  reviewArtwork: (assetId: string, decision: 'clean' | 'rejected', notes = '') =>
    request<{ reviewed: boolean }>(
      `/api/artwork/${encodeURIComponent(assetId)}/review`,
      { method: 'POST', body: { decision, notes } }),

  adAnomalies: (days = 30) =>
    request<{ anomalies: AnomalyRow[] }>(`/api/admin/ads/anomalies?days=${days}`),

  syncSlots: () =>
    request<{ result: { inserted: number; updated: number; retired: number } }>(
      '/api/admin/ads/slots/sync', { method: 'POST' }),

  // --- invoices ---
  myInvoices: () => request<{ invoices: InvoiceView[] }>('/api/billing/invoices'),

  myInvoice: (id: string) =>
    request<{ invoice: InvoiceView }>(`/api/billing/invoices/${encodeURIComponent(id)}`),

  allInvoices: () => request<{ invoices: InvoiceView[] }>('/api/billing/all-invoices'),

  invoiceSettings: () => request<{ settings: InvoiceSettings }>('/api/billing/settings'),

  saveInvoiceSettings: (settings: Partial<InvoiceSettings>) =>
    request<{ settings: InvoiceSettings }>('/api/billing/settings',
      { method: 'PATCH', body: settings }),

  billableWork: (advertiserId: string, from: string, to: string) =>
    request<{ lines: BillableLine[] }>(
      `/api/billing/billable?advertiserId=${encodeURIComponent(advertiserId)}` +
      `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

  draftInvoice: (advertiserId: string, periodStart: string, periodEnd: string, notes = '') =>
    request<{ invoice: InvoiceView }>('/api/billing/invoices',
      { method: 'POST', body: { advertiserId, periodStart, periodEnd, notes } }),

  issueInvoice: (id: string, dueDays = 14) =>
    request<{ issued: boolean }>(
      `/api/billing/invoices/${encodeURIComponent(id)}/issue`,
      { method: 'POST', body: { dueDays } }),

  markInvoicePaid: (id: string) =>
    request<{ paid: boolean }>(
      `/api/billing/invoices/${encodeURIComponent(id)}/paid`, { method: 'POST' }),

  voidInvoice: (id: string, reason: string) =>
    request<{ voided: boolean }>(
      `/api/billing/invoices/${encodeURIComponent(id)}/void`,
      { method: 'POST', body: { reason } }),

  resetPassword: (token: string, password: string) =>
    request<{ reset: boolean }>('/api/auth/reset-password', {
      method: 'POST',
      auth: false,
      body: { token, password }
    }),

  forgotPassword: (email: string) =>
    request<{ sent: boolean; message: string; devResetUrl?: string }>('/api/auth/forgot-password', {
      method: 'POST',
      auth: false,
      body: { email }
    })
};
